import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { View, Pressable, Platform, Linking, ScrollView as RNScrollView, TextInput, Image, ViewStyle } from 'react-native';
import { Title, Text, ScrollView } from '@constants/Themed';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import apis from '@api/api';
import * as ImagePicker from 'expo-image-picker';
import { manipulateAsync, SaveFormat } from 'expo-image-manipulator';
import { CLEAR_AVATAR, SHOW_AUTH_MODAL, AVATAR_UPDATED } from '@reduxLocal/actions/types';
import AnimatedLottieView from '@utils/lottieAnimation';
import GenerateSchemaForm from '@hooks/generateSchemaForm';
import useGlobalUser from "@hooks/getGlobalUser";
import toTitleCase from '@hooks/toTitleCase';
import { useDispatch, useSelector } from 'react-redux';
import SmallActionButton from '@components/UI/SmallActionButton';
import { H4, H6, Separator, XStack, YStack, Tabs, SizableText } from 'tamagui';
import PlanComponent from '../../UpgradeScreen/Components/PlanComponent';
import { debounce } from 'lodash';
import PassComponent from './PassComponent';
import { useDeviceType } from '@hooks/useDeviceType';
import UserSettings from './dom-component';
import { Card } from '@constants/Themed';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { AntDesign, Feather } from '@expo/vector-icons';
import { AffiliateComponent } from '../AffiliateScreen';
import { schemaCache } from '@utils/schemaCache';
import { DefaultRootState } from '@reduxLocal/persistState';
import MfaComponent from './MfaComponent';
import RowComponent from '@components/Global/RowComponent/RowComponent';
import { useTranslation } from 'react-i18next';
import ErrorBoundary from '@utils/ErrorBoundary';

const UsageStatMonitor = React.lazy(() => import('../Components/UsageStatMonitor'));

const ProfileSection = ({ user, company }) => {
  const globalId = useGlobalUser();
  const [flip, setFlip] = useState(false);
  const upgradeRef = useRef(null);
  const dispatch = useDispatch();
  const billingManageRef = useRef(null);
  const [userPlan, setUserPlan] = useState(null);
  const { isTablet } = useDeviceType();
  const theme = themeSelector();
  const currentCompany = useSelector((state: DefaultRootState) => state.app.selectedProfile);
  const { t } = useTranslation();
  // Collapsible states for mobile
  const [showPersonalInfo, setShowPersonalInfo] = useState(true);
  const [showQuickStats, setShowQuickStats] = useState(true);
  const [showSocialMedia, setShowSocialMedia] = useState(true);

  // Social media editing state
  const [isEditingSocialMedia, setIsEditingSocialMedia] = useState(false);
  const [editedSocialMedia, setEditedSocialMedia] = useState({});

  // Personal info editing state
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editedValues, setEditedValues] = useState<Record<string, string>>({});

  // Use cached schema instead of fetching
  const data = schemaCache.getSchema('profileSchema');
  const isLoading = !data; // Schema is loading if not in cache yet

  // Query for social media data
  const {
    data: socialMediaData,
    isLoading: socialMediaLoading,
    refetch: refetchSocialMedia
  } = useQuery({
    queryKey: ['userSocialMedia', globalId],
    queryFn: async () => {
      const response = await apis.getUserSocialMedia();
      return response.data?.data;
    },
    retry: 2,
    staleTime: 1000 * 60 * 10, // 10 minutes
    enabled: !!globalId
  });

  // Initialize edited social media when data loads
  useEffect(() => {
    if (socialMediaData?.socialMedia) {
      setEditedSocialMedia(socialMediaData.socialMedia);
    }
  }, [socialMediaData]);

  // useEffect(() => {
  //   (async () => {
  //     if (Platform.OS != 'web') {
  //       const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
  //       if (status != 'granted') {
  //         alert('Camera roll permissions required');
  //       }
  //     }
  //   })();


  const _resizeImage = async () => {
    const pickImage = async () => {
      let result: ImagePicker.ImagePickerResult;
      const permissionResult = await ImagePicker.requestMediaLibraryPermissionsAsync();

      if (permissionResult.granted === false) {
        alert("Permission to access camera roll is required to select profile picture!", [{
          text: 'OK', onPress: async () => {
            await ImagePicker.requestMediaLibraryPermissionsAsync();
          }
        }]);
        return;
      }

      result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        base64: true,
        allowsEditing: true,
        aspect: [4, 3],
        quality: 1,
      });

      if (!result.canceled) {
        return result;
      }
      if (result.canceled) {
        alert("Upload canceled");
        return;
      }
      return result;
    };

    pickImage()
      .then(async result => {
        const { assets } = result;
        try {
          const uri = assets[0].uri;
          const manipResultImg = await manipulateAsync(uri, [
            {
              resize: { height: 500 }
            }
          ], { format: SaveFormat.PNG, compress: 1, base64: true }
          );
          let img = manipResultImg.base64;
          await apis.postAvatar(user?.id, { img })
            .then(res => {
              if (res.status == 200) {
                dispatch({ type: CLEAR_AVATAR })
                dispatch({ type: AVATAR_UPDATED, data: res.data.data })
              }
            })
        } catch (e) {
          dispatch({ type: SHOW_AUTH_MODAL, data: { content: 'Error uploading image' + e.toString(), type: 'error' } })
        }
      })
      .catch((e) => {
        console.error(e)
      });
  }
  const customStyling = useMemo(() => ({
    flex: 1,
    paddingBottom: 10,
    maxHeight: "60vh"
  }), []);

  const MemoizedGenerateSchemaForm = React.memo(GenerateSchemaForm);

  const _planCallback = useCallback((plan) => {
    setUserPlan(plan);
  }, [userPlan]);

  // Handle social media save
  const handleSaveSocialMedia = useCallback(async () => {
    try {
      await apis.updateUserSocialMedia(editedSocialMedia);
      setIsEditingSocialMedia(false);
      refetchSocialMedia();
      // Show success message
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: {
          content: 'Social media links updated successfully!',
          type: 'success'
        }
      });
    } catch (error) {
      console.error('Error updating social media:', error);
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: {
          content: 'Failed to update social media links',
          type: 'error'
        }
      });
    }
  }, [editedSocialMedia, refetchSocialMedia, dispatch]);

  // Helper to get user profile data with fallbacks
  const getProfileData = useCallback((field, fallback = '') => {
    return user?.profile_information?.[field] || user?.[field] || fallback;
  }, [user]);

  // Handle field edit start
  const handleEditField = useCallback((fieldKey: string, currentValue: string) => {
    setEditingField(fieldKey);
    setEditedValues({ [fieldKey]: currentValue });
  }, []);

  // Handle field value change
  const handleFieldChange = useCallback((fieldKey: string, value: string) => {
    setEditedValues(prev => ({ ...prev, [fieldKey]: value }));
  }, []);

  const getCardStyle = (theme?: boolean): ViewStyle => ({
    backgroundColor: theme ? 'rgba(248, 250, 252, 0.8)' : 'rgba(30, 30, 30, 0.8)',
    borderRadius: 16,
    padding: 20,
    borderWidth: 1,
    borderColor: theme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)',
  });

  // Handle field save
  const handleSaveField = useCallback(async (fieldKey: string) => {
    try {
      const valueToSave = editedValues[fieldKey];

      // Map field keys to the correct profile_information fields
      const fieldMapping: Record<string, string> = {
        'name': 'name',
        'surname': 'surname',
        'mobile': 'mobile',
        'department': 'department',
        'job_title': 'job_title',
        'city': 'city',
        'country': 'country'
      };

      const payload = {
        content: {
          [fieldMapping[fieldKey] || fieldKey]: valueToSave
        }
      };

      const response = await apis.updateUserProfile(payload);

      if (response?.success) {
        setEditingField(null);
        setEditedValues({});

        // Update Redux state with new value
        dispatch({
          type: 'UPDATE_USER_PROFILE',
          payload: {
            [fieldMapping[fieldKey] || fieldKey]: valueToSave
          }
        });

        dispatch({
          type: SHOW_AUTH_MODAL,
          data: {
            content: 'Profile updated successfully!',
            type: 'success'
          }
        });

        // Optionally reload page on web only
        if (Platform.OS === 'web') {
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        }
      }

    } catch (error) {
      console.error('Error updating field:', error);
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: {
          content: 'Failed to update profile',
          type: 'error'
        }
      });
    }
  }, [editedValues, dispatch]);

  // Handle field cancel
  const handleCancelEdit = useCallback(() => {
    setEditingField(null);
    setEditedValues({});
  }, []);

  // Social media platform configuration
  const socialMediaPlatforms = {
    facebook: { icon: 'facebook-square', color: '#1877F2', label: 'Facebook' },
    twitter: { icon: 'twitter', color: '#1DA1F2', label: 'Twitter' },
    linkedin: { icon: 'linkedin-square', color: '#0A66C2', label: 'LinkedIn' },
    instagram: { icon: 'instagram', color: '#E4405F', label: 'Instagram' },
    youtube: { icon: 'youtube', color: '#FF0000', label: 'YouTube' },
    pinterest: { icon: 'pinterest', color: '#BD081C', label: 'Pinterest' },
    tiktok: { icon: 'video-camera', color: '#000000', label: 'TikTok' },
    twitch: { icon: 'play-circle', color: '#9146FF', label: 'Twitch' },
    reddit: { icon: 'reddit-alien', color: '#FF4500', label: 'Reddit' },
    medium: { icon: 'medium', color: '#000000', label: 'Medium' },
    github: { icon: 'github', color: '#333333', label: 'GitHub' },
    behance: { icon: 'behance-square', color: '#1769FF', label: 'Behance' },
    dribbble: { icon: 'dribbble', color: '#EA4C89', label: 'Dribbble' },
    spotify: { icon: 'spotify', color: '#1DB954', label: 'Spotify' },
    soundcloud: { icon: 'soundcloud', color: '#FF3300', label: 'SoundCloud' },
    discord: { icon: 'discord', color: '#5865F2', label: 'Discord' },
    whatsapp: { icon: 'whatsapp', color: '#25D366', label: 'WhatsApp' },
    telegram: { icon: 'telegram', color: '#0088CC', label: 'Telegram' },
  };

  // Function to handle social media link clicks
  const handleSocialMediaPress = useCallback(async (platform: string, url: string) => {
    try {
      // Add https:// if not present and it looks like a full URL
      let fullUrl = url;
      if (url.includes('.') && !url.startsWith('http')) {
        fullUrl = `https://${url}`;
      } else if (!url.includes('.') && !url.startsWith('http')) {
        // For username-only format, construct platform URL
        const platformUrls: Record<string, string> = {
          facebook: 'https://facebook.com/',
          twitter: 'https://twitter.com/',
          linkedin: 'https://linkedin.com/in/',
          instagram: 'https://instagram.com/',
          youtube: 'https://youtube.com/@',
          github: 'https://github.com/',
          tiktok: 'https://tiktok.com/@',
          discord: 'https://discord.gg/',
        };
        fullUrl = (platformUrls[platform] || 'https://') + url;
      }

      if (Platform.OS === 'web') {
        window.open(fullUrl, '_blank');
      } else {
        // For mobile platforms
        const canOpen = await Linking.canOpenURL(fullUrl);
        if (canOpen) {
          await Linking.openURL(fullUrl);
        } else {
          console.warn('Cannot open URL:', fullUrl);
        }
      }
    } catch (error) {
      console.error('Error opening social media link:', error);
    }
  }, []);

  const deviceList = [
    { name: 'Phone', icon: 'phone' },
    { name: 'Tablet', icon: 'tablet' },
    { name: 'Desktop', icon: 'desktop' },
  ]

  // Simple DeviceItem component
  const DeviceItem = ({ device }) => (
    <View style={{ gap: 20, flexDirection: 'row', alignItems: 'center', marginBottom: 8 }}>
      <AntDesign name={device.icon} size={20} color="#5ac0ea" />
      <Text>{device.name}</Text>
    </View>
  );

  // Collapsible Section Header Component
  const CollapsibleHeader = ({ title, isExpanded, onToggle }) => (
    <Pressable
      onPress={onToggle}
      style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: isExpanded ? 20 : 0,
      }}
    >
      <H6 style={{
        fontSize: 18,
        color: 'rgb(90, 192, 234)',
        fontWeight: 'bold',
      }}>
        {title}
      </H6>
      <AntDesign
        name={isExpanded ? "up" : "down"}
        size={16}
        color="rgb(90, 192, 234)"
      />
    </Pressable>
  );

  // Editable Field Component
  const EditableField = ({ label, value, fieldKey, isLast }: { label: string; value: string; fieldKey: string; isLast: boolean }) => {
    const isEditing = editingField === fieldKey;
    const displayValue = isEditing ? (editedValues[fieldKey] ?? value) : value;

    return (
      <View style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: 8,
        borderBottomWidth: isLast ? 0 : 1,
        borderBottomColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)',
        gap: 10,
      }}>
        <Text style={{
          fontWeight: '500',
          color: theme ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)',
          fontSize: 14,
          flex: 1,
          minWidth: 100,
        }}>
          {label}
        </Text>

        {isEditing ? (
          <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
            {Platform.OS === 'web' ? (
              <input
                type="text"
                value={displayValue}
                onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                style={{
                  flex: 1,
                  minWidth: '120px',
                  padding: '6px 10px',
                  borderRadius: '6px',
                  border: `1px solid ${theme ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.2)'}`,
                  backgroundColor: theme ? '#ffffff' : 'rgba(40, 40, 40, 0.8)',
                  color: theme ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                  fontSize: '14px',
                  outline: 'none',
                }}
              />
            ) : (
              <TextInput
                value={displayValue}
                onChangeText={(text) => handleFieldChange(fieldKey, text)}
                style={{
                  flex: 1,
                  minWidth: 120,
                  padding: 8,
                  borderRadius: 6,
                  borderWidth: 1,
                  borderColor: theme ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.2)',
                  backgroundColor: theme ? '#ffffff' : 'rgba(40, 40, 40, 0.8)',
                  color: theme ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                  fontSize: 14,
                }}
              />
            )}
            <Pressable
              onPress={() => handleSaveField(fieldKey)}
              style={{
                backgroundColor: 'rgb(90, 192, 234)',
                padding: 6,
                borderRadius: 6,
              }}
            >
              <AntDesign name="check" size={14} color="white" />
            </Pressable>
            <Pressable
              onPress={handleCancelEdit}
              style={{
                backgroundColor: theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
                padding: 6,
                borderRadius: 6,
              }}
            >
              <AntDesign name="close" size={14} color={theme ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)'} />
            </Pressable>
          </View>
        ) : (
          <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
            <Text style={{
              color: theme ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)',
              fontWeight: '600',
              fontSize: 14,
              textAlign: 'right',
            }}>
              {displayValue}
            </Text>
            <Pressable
              onPress={() => handleEditField(fieldKey, value)}
              style={{
                padding: 4,
              }}
            >
              <AntDesign name="edit" size={14} color="rgb(90, 192, 234)" />
            </Pressable>
          </View>
        )}
      </View>
    );
  };



  // Render the content
  const renderContent = () => (
    <XStack className="hide-scrollbar-virtuoso" style={{ overflowY: 'auto' }} flex={1} alignSelf="stretch" flexWrap="wrap" columnGap={30} rowGap={30}>
      {/* Profile Card - Left Side */}
      {isTablet || Platform.OS === 'web' ? (
        <ScrollView showsVerticalScrollIndicator={false} showsHorizontalScrollIndicator={false} style={{ flex: 1 }}  >
          <Card
            style={{
              flex: 1,
              backgroundColor: theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(40, 40, 40, 0.95)',
              borderRadius: 24,
              padding: 24,
              borderWidth: 1,
              alignSelf: 'stretch',
              alignItems: 'stretch',
              justifyContent: 'stretch',
              borderColor: theme ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)',
            }}
          >
            <XStack flex={1} alignSelf="stretch" gap={30} columnGap={30} flexWrap="wrap">
              {/* Profile Header */}
              <YStack style={{ flexGrow: 1, minWidth: 300 }}>
                <XStack>
                  <View style={{ flex: 3, alignItems: 'center', marginBottom: 32 }}>
                  <Pressable onPress={_resizeImage} style={{ position: 'relative' }}>
                    <Image
                      source={{ uri: user?.avatar?.img || user?.profile_information?.avatar?.img }}
                      style={{
                        borderWidth: 4,
                        borderColor: 'rgb(90, 192, 234)',
                        width: 120,
                        height: 120,
                        borderRadius: 60,
                        resizeMode: 'cover',
                        backgroundColor: theme ? '#f5f5f5' : '#404040',
                      }}
                    />
                    {/* Camera Icon Overlay */}
                    <View style={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      backgroundColor: 'rgb(90, 192, 234)',
                      borderRadius: 20,
                      width: 40,
                      height: 40,
                      justifyContent: 'center',
                      alignItems: 'center',
                      borderWidth: 3,
                      borderColor: theme ? '#fff' : '#2a2a2a',
                      shadowColor: '#000',
                      shadowOffset: { width: 0, height: 2 },
                      shadowOpacity: 0.2,
                      shadowRadius: 4,
                      elevation: 3,
                    }}>
                      <AntDesign name="camera" size={16} color="white" />
                    </View>
                  </Pressable>

                  <Title style={{
                    marginTop: 16,
                    marginBottom: 4,
                    textAlign: 'center',
                    fontSize: 24,
                    fontWeight: 'bold',
                    color: theme ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)'
                  }}>
                    {toTitleCase(`${getProfileData('name', '')} ${getProfileData('surname', '')}`.trim() || 'User')}
                  </Title>

                  <Text style={{
                    color: theme ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                    marginBottom: 8,
                    textAlign: 'center',
                    fontSize: 16
                  }}>
                    {user.email}
                  </Text>

                  {/* Status Badge */}
                  <View style={{
                    backgroundColor: theme ? 'rgba(90, 192, 234, 0.1)' : 'rgba(90, 192, 234, 0.2)',
                    borderRadius: 20,
                    paddingHorizontal: 12,
                    paddingVertical: 6,
                    borderWidth: 1,
                    borderColor: 'rgb(90, 192, 234)'
                  }}>
                    <Text style={{ color: 'rgb(90, 192, 234)', fontSize: 12, fontWeight: '600' }}>
                      Active Member
                    </Text>
                  </View>
                </View>
                {/* Quick Stats */}
                  <View style={{ flex: 1 }} >
                    <YStack gap={12}>
                    {[
                      { label: 'Projects', value: user.projectsCount || '12', color: 'rgb(90, 192, 234)' },
                      { label: 'Tasks', value: user.tasksCompleted || '48', color: 'rgb(34, 197, 94)' },
                      { label: 'Team', value: user.teamSize || '6', color: 'rgb(168, 85, 247)' }
                    ].map((stat, index) => (
                      <View key={index} style={{
                        flex: 1,
                        backgroundColor: theme ? 'rgba(248, 250, 252, 0.8)' : 'rgba(56, 56, 56, 0.8)',
                        borderRadius: 12,
                        padding: 12,
                        alignItems: 'center',
                        borderWidth: 1,
                        borderColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)'
                      }}>
                        <Text style={{
                          fontSize: 20,
                          fontWeight: 'bold',
                          color: stat.color
                        }}>
                          {stat.value}
                        </Text>
                        <Text style={{
                          fontSize: 12,
                          color: theme ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                          marginTop: 4,
                          fontWeight: '500'
                        }}>
                          {stat.label}
                        </Text>
                      </View>
                    ))}
                    </YStack>
                  </View>
                </XStack>
                {/* Social Media Section */}
                <View style={{ marginTop: 24 }}>
                  <H6 style={{
                    fontSize: 16,
                    marginBottom: 16,
                    color: 'rgb(90, 192, 234)',
                    fontWeight: 'bold',
                    textAlign: 'center'
                  }}>
                    Social Media
                  </H6>

                  {socialMediaLoading ? (
                    <View style={{
                      justifyContent: 'center',
                      alignItems: 'center',
                      padding: 20
                    }}>
                      <Text style={{
                        color: theme ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                        fontSize: 14
                      }}>
                        Loading social media...
                      </Text>
                    </View>
                  ) : socialMediaData?.socialMedia && Object.keys(socialMediaData.socialMedia).length > 0 ? (
                    <View style={{
                      flexDirection: 'row',
                      flexWrap: 'wrap',
                      gap: 12,
                      justifyContent: 'center'
                    }}>
                      {Object.entries(socialMediaData.socialMedia).map(([platform, url]) => {
                        const platformConfig = socialMediaPlatforms[platform as keyof typeof socialMediaPlatforms];
                        if (!platformConfig || !url) return null;

                        return (
                          <Pressable
                            key={platform}
                            onPress={() => handleSocialMediaPress(platform, url as string)}
                            style={({ pressed }) => [
                              {
                                backgroundColor: theme ? 'rgba(248, 250, 252, 0.8)' : 'rgba(56, 56, 56, 0.8)',
                                borderRadius: 12,
                                padding: 12,
                                alignItems: 'center',
                                justifyContent: 'center',
                                borderWidth: 1,
                                borderColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)',
                                minWidth: 80,
                                shadowColor: '#000',
                                shadowOffset: { width: 0, height: 2 },
                                shadowOpacity: theme ? 0.05 : 0.15,
                                shadowRadius: 4,
                                elevation: 2,
                                opacity: pressed ? 0.7 : 1,
                                transform: [{ scale: pressed ? 0.98 : 1 }],
                              }
                            ]}
                          >
                            <AntDesign
                              name={platformConfig.icon as any}
                              size={24}
                              color={platformConfig.color}
                            />
                            <Text style={{
                              fontSize: 10,
                              color: theme ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)',
                              marginTop: 4,
                              fontWeight: '500',
                              textAlign: 'center'
                            }}>
                              {platformConfig.label}
                            </Text>
                          </Pressable>
                        );
                      })}
                    </View>
                  ) : (
                    <View style={{
                      backgroundColor: theme ? 'rgba(248, 250, 252, 0.5)' : 'rgba(56, 56, 56, 0.5)',
                      borderRadius: 12,
                      padding: 16,
                      alignItems: 'center',
                      borderWidth: 1,
                      borderColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)',
                      borderStyle: 'dashed'
                    }}>
                      <AntDesign
                        name="plus-circle"
                        size={32}
                        color={theme ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.3)'}
                      />
                      <Text style={{
                        fontSize: 14,
                        color: theme ? 'rgba(0, 0, 0, 0.5)' : 'rgba(255, 255, 255, 0.5)',
                        marginTop: 8,
                        textAlign: 'center'
                      }}>
                        No social media links added
                      </Text>
                      <Text style={{
                        fontSize: 12,
                        color: theme ? 'rgba(0, 0, 0, 0.4)' : 'rgba(255, 255, 255, 0.4)',
                        marginTop: 4,
                        textAlign: 'center'
                      }}>
                        Update your profile to add links
                      </Text>
                    </View>
                  )}
                </View>
              </YStack>

              {/* Active Company Display */}
              {currentCompany && (
                <>
                  <View style={{
                    backgroundColor: theme ? 'rgba(248, 250, 252, 0.8)' : 'rgba(56, 56, 56, 0.8)',
                    borderRadius: 12,
                    padding: 16,
                    marginBottom: 24,
                    borderWidth: 1,
                    borderColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)',
                  }}>
                    <Text style={{
                      fontSize: 12,
                      fontWeight: '600',
                      color: theme ? 'rgba(0, 0, 0, 0.5)' : 'rgba(255, 255, 255, 0.5)',
                      marginBottom: 8,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5
                    }}>
                      Active Company Context
                    </Text>
                    <XStack alignItems="center" gap={12}>
                      {currentCompany.logo ? (
                        <Image
                          source={{ uri: currentCompany.logo }}
                          style={{
                            width: 32,
                            height: 32,
                            borderRadius: 8,
                            resizeMode: 'contain',
                            backgroundColor: theme ? '#f5f5f5' : '#404040',
                          }}
                        />
                      ) : (
                        <View style={{
                          width: 32,
                          height: 32,
                          borderRadius: 8,
                          backgroundColor: 'rgb(90, 192, 234)',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <AntDesign name="bank" size={16} color="white" />
                        </View>
                      )}
                      <View style={{ flex: 1 }}>
                        <Text style={{
                          fontSize: 14,
                          fontWeight: '700',
                          color: theme ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                          marginBottom: 2
                        }}>
                          {toTitleCase(currentCompany.companyName || '')}
                        </Text>
                        <Text style={{
                          fontSize: 11,
                          color: 'rgb(90, 192, 234)',
                          fontWeight: '600'
                        }}>
                          Company Account
                        </Text>
                      </View>
                    </XStack>
                  </View>
                </>
              )}
              {/* Personal Information Section */}
              <View style={{ flexGrow: 1, minWidth: 300 }}>
                <H6 style={{
                  fontSize: 18,
                  marginBottom: 20,
                  color: 'rgb(90, 192, 234)',
                  fontWeight: 'bold',
                  textAlign: 'center'
                }}>
                  Personal Information
                </H6>

                <YStack alignSelf="stretch" flex={1} gap={0}>
                  <EditableField
                    label="First Name"
                    value={getProfileData('name', 'Not set')}
                    fieldKey="name"
                    isLast={false}
                  />
                  <EditableField
                    label="Last Name"
                    value={getProfileData('surname', 'Not set')}
                    fieldKey="surname"
                    isLast={false}
                  />
                  <View style={{
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingVertical: 8,
                    borderBottomWidth: 1,
                    borderBottomColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)',
                  }}>
                    <Text style={{
                      fontWeight: '500',
                      color: theme ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                      fontSize: 14,
                      flex: 1,
                    }}>
                      Email
                    </Text>
                    <Text style={{
                      color: theme ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)',
                      fontWeight: '600',
                      fontSize: 14,
                      flex: 1,
                      textAlign: 'right',
                    }}>
                      {user.email || 'Not set'}
                    </Text>
                  </View>
                  <EditableField
                    label="Phone"
                    value={getProfileData('mobile', 'Not set')}
                    fieldKey="mobile"
                    isLast={false}
                  />
                  <EditableField
                    label="Department"
                    value={getProfileData('department', 'Not set')}
                    fieldKey="department"

                    isLast={false}
                  />
                  <EditableField
                    label="Position"
                    value={getProfileData('job_title', getProfileData('role', 'Not set'))}
                    fieldKey="job_title"
                    isLast={false}
                  />
                  <EditableField
                    label="City"
                    value={getProfileData('city', 'Not set')}
                    fieldKey="city"
                    isLast={false}
                  />
                  <EditableField
                    label="Country"
                    value={getProfileData('country', 'Not set')}
                    fieldKey="country"
                    isLast={true}
                  />
                </YStack>
              </View>
            </XStack>
          </Card>
        </ScrollView>
      ) : (
        // On mobile, render without ScrollView wrapper to avoid nesting
          <ScrollView showsVerticalScrollIndicator={false} showsHorizontalScrollIndicator={false} style={{ flex: 1 }}  >
        <Card
          style={{
            flex: 1,
            maxWidth: isTablet ? 420 : '100%',
            backgroundColor: theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(40, 40, 40, 0.95)',
            borderRadius: 24,
            padding: 24,
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: theme ? 0.15 : 0.25,
            shadowRadius: 16,
            elevation: 10,
            borderWidth: 1,
            borderColor: theme ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)',
          }}
        >
          {/* Profile Header */}
          <View style={{ alignItems: 'center', marginBottom: 32 }}>
            <Pressable onPress={_resizeImage} style={{ position: 'relative' }}>
              <Image
                source={{ uri: user?.avatar?.img || user?.profile_information?.avatar?.img }}
                style={{
                  borderWidth: 4,
                  borderColor: 'rgb(90, 192, 234)',
                  width: 120,
                  height: 120,
                  borderRadius: 60,
                  resizeMode: 'cover',
                  backgroundColor: theme ? '#f5f5f5' : '#404040',
                }}
              />
              {/* Camera Icon Overlay */}
              <View style={{
                position: 'absolute',
                bottom: 0,
                right: 0,
                backgroundColor: 'rgb(90, 192, 234)',
                borderRadius: 20,
                width: 40,
                height: 40,
                justifyContent: 'center',
                alignItems: 'center',
                borderWidth: 3,
                borderColor: theme ? '#fff' : '#2a2a2a',
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: 0.2,
                shadowRadius: 4,
                elevation: 3,
              }}>
                <AntDesign name="camera" size={16} color="white" />
              </View>
            </Pressable>

            <Title style={{
              marginTop: 16,
              marginBottom: 4,
              textAlign: 'center',
              fontSize: 24,
              fontWeight: 'bold',
              color: theme ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)'
            }}>
              {toTitleCase(`${getProfileData('name', '')} ${getProfileData('surname', '')}`.trim() || 'User')}
            </Title>

            <Text style={{
              color: theme ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)',
              marginBottom: 8,
              textAlign: 'center',
              fontSize: 16
            }}>
              {user.email}
            </Text>

            {/* Status Badge */}
            <View style={{
              backgroundColor: theme ? 'rgba(90, 192, 234, 0.1)' : 'rgba(90, 192, 234, 0.2)',
              borderRadius: 20,
              paddingHorizontal: 12,
              paddingVertical: 6,
              borderWidth: 1,
              borderColor: 'rgb(90, 192, 234)'
            }}>
              <Text style={{ color: 'rgb(90, 192, 234)', fontSize: 12, fontWeight: '600' }}>
                Active Member
              </Text>
            </View>
          </View>

          <Separator backgroundColor={theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)'} marginBottom={24} />

          {/* Active Company Display */}
          {currentCompany && (
            <>
              <View style={{
                backgroundColor: theme ? 'rgba(248, 250, 252, 0.8)' : 'rgba(56, 56, 56, 0.8)',
                borderRadius: 12,
                padding: 16,
                marginBottom: 24,
                borderWidth: 1,
                borderColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)',
              }}>
                <Text style={{
                  fontSize: 12,
                  fontWeight: '600',
                  color: theme ? 'rgba(0, 0, 0, 0.5)' : 'rgba(255, 255, 255, 0.5)',
                  marginBottom: 8,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5
                }}>
                  Active Company Context
                </Text>
                <XStack alignItems="center" gap={12}>
                  {currentCompany.logo ? (
                    <Image
                      source={{ uri: currentCompany.logo }}
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 8,
                        resizeMode: 'contain',
                        backgroundColor: theme ? '#f5f5f5' : '#404040',
                      }}
                    />
                  ) : (
                    <View style={{
                      width: 32,
                      height: 32,
                      borderRadius: 8,
                      backgroundColor: 'rgb(90, 192, 234)',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <AntDesign name="bank" size={16} color="white" />
                    </View>
                  )}
                  <View style={{ flex: 1 }}>
                    <Text style={{
                      fontSize: 14,
                      fontWeight: '700',
                      color: theme ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                      marginBottom: 2
                    }}>
                      {toTitleCase(currentCompany.companyName || '')}
                    </Text>
                    <Text style={{
                      fontSize: 11,
                      color: 'rgb(90, 192, 234)',
                      fontWeight: '600'
                    }}>
                      Company Account
                    </Text>
                  </View>
                </XStack>
              </View>
            </>
          )}

          {/* Personal Information Section */}
          <View style={{ marginBottom: 16 }}>
            <CollapsibleHeader
              title="Personal Information"
              isExpanded={showPersonalInfo}
              onToggle={() => setShowPersonalInfo(!showPersonalInfo)}
            />

            {showPersonalInfo && (
              <YStack gap={0}>
                <EditableField
                  label="First Name"
                  value={getProfileData('name', 'Not set')}
                  fieldKey="name"
                  isLast={false}
                />
                <EditableField
                  label="Last Name"
                  value={getProfileData('surname', 'Not set')}
                  fieldKey="surname"
                  isLast={false}
                />
                <View style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingVertical: 8,
                  borderBottomWidth: 1,
                  borderBottomColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)',
                }}>
                  <Text style={{
                    fontWeight: '500',
                    color: theme ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                    fontSize: 14,
                    flex: 1,
                  }}>
                    Email
                  </Text>
                  <Text style={{
                    color: theme ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)',
                    fontWeight: '600',
                    fontSize: 14,
                    flex: 1,
                    textAlign: 'right',
                  }}>
                    {user.email || 'Not set'}
                  </Text>
                </View>
                <EditableField
                  label="Phone"
                  value={getProfileData('mobile', 'Not set')}
                  fieldKey="mobile"
                  isLast={false}
                />
                <EditableField
                  label="Department"
                  value={getProfileData('department', 'Not set')}
                  fieldKey="department"
                  isLast={false}
                />
                <EditableField
                  label="Position"
                  value={getProfileData('job_title', getProfileData('role', 'Not set'))}
                  fieldKey="job_title"
                  isLast={false}
                />
                <EditableField
                  label="City"
                  value={getProfileData('city', 'Not set')}
                  fieldKey="city"
                  isLast={false}
                />
                <EditableField
                  label="Country"
                  value={getProfileData('country', 'Not set')}
                  fieldKey="country"
                  isLast={true}
                />
              </YStack>
            )}
          </View>

          {/* Quick Stats */}
          <View style={{ marginBottom: 16 }}>
            <CollapsibleHeader
              title="Activity Overview"
              isExpanded={showQuickStats}
              onToggle={() => setShowQuickStats(!showQuickStats)}
            />

            {showQuickStats && (
              <XStack gap={12}>
                {[
                  { label: 'Projects', value: user.projectsCount || '12', color: 'rgb(90, 192, 234)' },
                  { label: 'Tasks', value: user.tasksCompleted || '48', color: 'rgb(34, 197, 94)' },
                  { label: 'Team', value: user.teamSize || '6', color: 'rgb(168, 85, 247)' }
                ].map((stat, index) => (
                  <View key={index} style={{
                    flex: 1,
                    backgroundColor: theme ? 'rgba(248, 250, 252, 0.8)' : 'rgba(56, 56, 56, 0.8)',
                    borderRadius: 12,
                    padding: 12,
                    alignItems: 'center',
                    borderWidth: 1,
                    borderColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)'
                  }}>
                    <Text style={{
                      fontSize: 20,
                      fontWeight: 'bold',
                      color: stat.color
                    }}>
                      {stat.value}
                    </Text>
                    <Text style={{
                      fontSize: 12,
                      color: theme ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                      marginTop: 4,
                      fontWeight: '500'
                    }}>
                      {stat.label}
                    </Text>
                  </View>
                ))}
              </XStack>
            )}
          </View>

          {/* Social Media Section */}
          <View style={{ marginBottom: 16 }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: showSocialMedia ? 20 : 0 }}>
              <Pressable
                onPress={() => setShowSocialMedia(!showSocialMedia)}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  flex: 1,
                }}
              >
                <H6 style={{
                  fontSize: 18,
                  color: 'rgb(90, 192, 234)',
                  fontWeight: 'bold',
                  marginRight: 10,
                }}>
                  Social Media
                </H6>
                <AntDesign
                  name={showSocialMedia ? "up" : "down"}
                  size={16}
                  color="rgb(90, 192, 234)"
                />
              </Pressable>

              {showSocialMedia && (
                <Pressable
                  onPress={() => {
                    if (isEditingSocialMedia) {
                      handleSaveSocialMedia();
                    } else {
                      setIsEditingSocialMedia(true);
                    }
                  }}
                  style={{
                    backgroundColor: 'rgb(90, 192, 234)',
                    paddingHorizontal: 12,
                    paddingVertical: 6,
                    borderRadius: 8,
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <AntDesign
                    name={isEditingSocialMedia ? "save" : "edit"}
                    size={14}
                    color="white"
                  />
                  <Text style={{ color: 'white', fontSize: 12, fontWeight: '600' }}>
                    {isEditingSocialMedia ? 'Save' : 'Edit'}
                  </Text>
                </Pressable>
              )}
            </View>

            {showSocialMedia && (isEditingSocialMedia ? (
              // Edit Mode
              <YStack gap={12}>
                {Object.entries(socialMediaPlatforms).map(([platform, config]) => (
                  <View key={platform} style={{
                    backgroundColor: theme ? 'rgba(248, 250, 252, 0.8)' : 'rgba(56, 56, 56, 0.8)',
                    borderRadius: 12,
                    padding: 12,
                    borderWidth: 1,
                    borderColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)',
                  }}>
                    <XStack alignItems="center" gap={10} marginBottom={8}>
                      <AntDesign
                        name={config.icon as any}
                        size={20}
                        color={config.color}
                      />
                      <Text style={{
                        fontSize: 14,
                        fontWeight: '600',
                        color: theme ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)',
                      }}>
                        {config.label}
                      </Text>
                    </XStack>
                    {Platform.OS === 'web' ? (
                      <input
                        type="text"
                        placeholder={`Enter your ${config.label} URL or username`}
                        value={editedSocialMedia[platform] || ''}
                        onChange={(e) => setEditedSocialMedia(prev => ({
                          ...prev,
                          [platform]: e.target.value
                        }))}
                        style={{
                          width: '100%',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: `1px solid ${theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)'}`,
                          backgroundColor: theme ? '#ffffff' : 'rgba(40, 40, 40, 0.8)',
                          color: theme ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                          fontSize: '14px',
                          outline: 'none',
                        }}
                      />
                    ) : (
                      <TextInput
                        placeholder={`Enter your ${config.label} URL or username`}
                        placeholderTextColor={theme ? 'rgba(0, 0, 0, 0.4)' : 'rgba(255, 255, 255, 0.4)'}
                        value={editedSocialMedia[platform] || ''}
                        onChangeText={(text) => setEditedSocialMedia(prev => ({
                          ...prev,
                          [platform]: text
                        }))}
                        style={{
                          width: '100%',
                          padding: 10,
                          borderRadius: 8,
                          borderWidth: 1,
                          borderColor: theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
                          backgroundColor: theme ? '#ffffff' : 'rgba(40, 40, 40, 0.8)',
                          color: theme ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)',
                          fontSize: 14,
                        }}
                      />
                    )}
                  </View>
                ))}
                <XStack gap={10} marginTop={10}>
                  <Pressable
                    onPress={() => {
                      setIsEditingSocialMedia(false);
                      setEditedSocialMedia(socialMediaData?.socialMedia || {});
                    }}
                    style={{
                      flex: 1,
                      backgroundColor: theme ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
                      paddingVertical: 10,
                      borderRadius: 10,
                      alignItems: 'center',
                    }}
                  >
                    <Text style={{ color: theme ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)', fontWeight: '600' }}>
                      Cancel
                    </Text>
                  </Pressable>
                </XStack>
              </YStack>
            ) : socialMediaLoading ? (
              <View style={{
                justifyContent: 'center',
                alignItems: 'center',
                padding: 20
              }}>
                <Text style={{
                  color: theme ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                  fontSize: 14
                }}>
                  Loading social media...
                </Text>
              </View>
            ) : socialMediaData?.socialMedia && Object.keys(socialMediaData.socialMedia).length > 0 ? (
              <View style={{
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: 12,
                justifyContent: 'center'
              }}>
                {Object.entries(socialMediaData.socialMedia).map(([platform, url]) => {
                  const platformConfig = socialMediaPlatforms[platform as keyof typeof socialMediaPlatforms];
                  if (!platformConfig || !url) return null;

                  return (
                    <Pressable
                      key={platform}
                      onPress={() => handleSocialMediaPress(platform, url as string)}
                      style={({ pressed }) => [
                        {
                          backgroundColor: theme ? 'rgba(248, 250, 252, 0.8)' : 'rgba(56, 56, 56, 0.8)',
                          borderRadius: 12,
                          padding: 12,
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderWidth: 1,
                          borderColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)',
                          minWidth: 80,
                          shadowColor: '#000',
                          shadowOffset: { width: 0, height: 2 },
                          shadowOpacity: theme ? 0.05 : 0.15,
                          shadowRadius: 4,
                          elevation: 2,
                          opacity: pressed ? 0.7 : 1,
                          transform: [{ scale: pressed ? 0.98 : 1 }],
                        }
                      ]}
                    >
                      <AntDesign
                        name={platformConfig.icon as any}
                        size={24}
                        color={platformConfig.color}
                      />
                      <Text style={{
                        fontSize: 10,
                        color: theme ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)',
                        marginTop: 4,
                        fontWeight: '500',
                        textAlign: 'center'
                      }}>
                        {platformConfig.label}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
            ) : (
              <View style={{
                backgroundColor: theme ? 'rgba(248, 250, 252, 0.5)' : 'rgba(56, 56, 56, 0.5)',
                borderRadius: 12,
                padding: 16,
                alignItems: 'center',
                borderWidth: 1,
                borderColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)',
                borderStyle: 'dashed'
              }}>
                <AntDesign
                  name="plus-circle"
                  size={32}
                  color={theme ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.3)'}
                />
                <Text style={{
                  fontSize: 14,
                  color: theme ? 'rgba(0, 0, 0, 0.5)' : 'rgba(255, 255, 255, 0.5)',
                  marginTop: 8,
                  textAlign: 'center'
                }}>
                  No social media links added
                </Text>
                <Text style={{
                  fontSize: 12,
                  color: theme ? 'rgba(0, 0, 0, 0.4)' : 'rgba(255, 255, 255, 0.4)',
                  marginTop: 4,
                  textAlign: 'center'
                }}>
                  Update your profile to add links
                </Text>
              </View>
            ))}
          </View>
        </Card>
          </ScrollView>
      )}
      {/* Tabs Component - Right Side */}
      {
        Platform.OS === 'web' &&
        <ScrollView style={{ flex: 1.5, alignSelf: 'stretch' }}>
      <Card
        style={{
                flex: isTablet || Platform.OS === 'web' ? 1 : 1,
                backgroundColor: theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(40, 40, 40, 0.95)',
          borderRadius: 24,
                padding: 24,
          borderWidth: 1,
          borderColor: theme ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.06)',
        }}
      >
        <Tabs
          defaultValue="profile"
          orientation="horizontal"
          flexDirection="column"
          width="100%"
          style={{
            backgroundColor: 'transparent',
          }}
        >
          {/* Wrap Tabs.List in a horizontal ScrollView for mobile */}
          <RNScrollView
            horizontal
            showsHorizontalScrollIndicator={!isTablet}
            style={{ minHeight: 60, maxHeight: 60 }}
            contentContainerStyle={{
              paddingHorizontal: !isTablet ? 10 : 0,
              minWidth: isTablet ? '100%' : undefined
            }}
          >
            <Tabs.List
              backgroundColor={theme ? 'rgba(248, 250, 252, 0.8)' : 'rgba(30, 30, 30, 0.8)'}
              borderRadius="$4"
              padding="$1"
              gap="$2"
              style={{
                marginBottom: 20,
                minHeight: 55,
                shadowColor: '#000',
                shadowOffset: { width: 0, height: 2 },
                shadowOpacity: theme ? 0.05 : 0.15,
                shadowRadius: 8,
                elevation: 2,
                flexWrap: 'nowrap',
                minWidth: isTablet ? '100%' : undefined,
              }}
            >
              <Tabs.Tab
                flex={isTablet ? 1 : undefined}
                minWidth={!isTablet ? 140 : undefined}
                value="profile"
                backgroundColor="transparent"
                borderRadius="$3"
                paddingHorizontal="$4"
                paddingVertical="$3"
                style={{
                  borderWidth: 0,
                }}
              >
                <XStack alignItems="center" gap="$2">
                  <AntDesign
                    name="user"
                    size={16}
                    color={theme ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)'}
                  />
                  <SizableText
                    size="$3"
                    fontWeight="500"
                    color={theme ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)'}
                  >
                    Edit Profile
                  </SizableText>
                </XStack>
              </Tabs.Tab>
              {
                Platform.OS === 'web' &&
                <Tabs.Tab
                  flex={isTablet ? 1 : undefined}
                  minWidth={!isTablet ? 160 : undefined}
                  value="billing"
                  backgroundColor="transparent"
                  borderRadius="$3"
                  paddingHorizontal="$4"
                  paddingVertical="$3"
                  style={{
                    borderWidth: 0,
                  }}
                >
                  <XStack alignItems="center" gap="$2">
                    <AntDesign
                      name="credit-card"
                      size={16}
                      color={theme ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)'}
                    />
                    <SizableText
                      size="$3"
                      fontWeight="500"
                      color={theme ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)'}
                    >
                      Plans & Billing
                    </SizableText>
                  </XStack>
                </Tabs.Tab>
              }

              <Tabs.Tab
                flex={isTablet ? 1 : undefined}
                minWidth={!isTablet ? 120 : undefined}
                value="security"
                backgroundColor="transparent"
                borderRadius="$3"
                paddingHorizontal="$4"
                paddingVertical="$3"
                style={{
                  borderWidth: 0,
                }}
              >
                <XStack alignItems="center" gap="$2">
                  <AntDesign
                    name="lock"
                    size={16}
                    color={theme ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)'}
                  />
                  <SizableText
                    size="$3"
                    fontWeight="500"
                    color={theme ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)'}
                  >
                    Security
                  </SizableText>
                </XStack>
              </Tabs.Tab>

              {
                Platform.OS === 'web' &&
                <Tabs.Tab
                  flex={isTablet ? 1 : undefined}
                  minWidth={!isTablet ? 130 : undefined}
                  value="analytics"
                  backgroundColor="transparent"
                  borderRadius="$3"
                  paddingHorizontal="$4"
                  paddingVertical="$3"
                  style={{
                    borderWidth: 0,
                  }}
                >
                  <XStack alignItems="center" gap="$2">
                    <AntDesign
                      name="bar-chart"
                      size={16}
                      color={theme ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)'}
                    />
                    <SizableText
                      size="$3"
                      fontWeight="500"
                      color={theme ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.7)'}
                    >
                      Analytics
                    </SizableText>
                  </XStack>
                </Tabs.Tab>
              }
            </Tabs.List>
          </RNScrollView>

          {/* Edit Profile Tab */}
          <Tabs.Content value="profile" flex={1}>
            <YStack maxHeight="80vh" gap="$4">
              <H6 style={{
                fontSize: 20,
                color: 'rgb(90, 192, 234)',
                fontWeight: 'bold',
                marginBottom: 8,
              }}>
                Profile Settings
              </H6>

              {!isLoading ? (
                <MemoizedGenerateSchemaForm
                  noBackground
                  hideLabel={false}
                  Progress={false}
                  schema={data?.schema}
                  containerWidth={undefined}
                  scrollEnabled={true}
                  avoidKeyboardDisabled
                  customStyling={{
                    flex: 1,
                    paddingBottom: 10,
                    maxHeight: isTablet || Platform.OS === 'web' ? "85vh" : undefined
                  }}
                  buttonStyle={{
                    alignSelf: 'center',
                    minHeight: 44,
                    borderColor: 'rgb(90, 192, 234)',
                    backgroundColor: 'rgb(90, 192, 234)',
                    marginTop: 24,
                    borderRadius: 16,
                    paddingHorizontal: 32,
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: theme ? 0.15 : 0.25,
                    shadowRadius: 8,
                    elevation: 3,
                  }}
                  submitFunction={apis.updateUserProfile}
                  nullify={['avatar', 'context', 'appReviewCompleted', 'plan', 'dashboardImage', 'aiEnabled', 'hasStore']}
                  retainInitialValues
                  buttonLabel='Update Profile'
                  callback={{ state: flip, setState: setFlip }}
                  initialData={{
                    ...user?.profile_information,
                    email: user?.email,
                    _id: user?._id
                  }}
                />
              ) : (
                <View style={{ alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
                  <AnimatedLottieView
                    source={require("@assets/animations/leaf_Loader.json")}
                    autoPlay
                    style={{
                      alignSelf: "center",
                      justifyContent: "center",
                      minHeight: 120,
                      minWidth: 120,
                      maxWidth: 180
                    }}
                    loop
                  />
                  <Text style={{
                    marginTop: 16,
                    color: theme ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                    fontSize: 16
                  }}>
                    Loading profile settings...
                  </Text>
                </View>
              )}
            </YStack>
          </Tabs.Content>

          {/* Plans & Billing Tab */}
          <Tabs.Content value="billing" flex={1}>
            <YStack gap="$4">
              <H6 style={{
                fontSize: 20,
                color: 'rgb(90, 192, 234)',
                fontWeight: 'bold',
                marginBottom: 8
              }}>
                Plans & Billing
              </H6>

              <YStack gap="$3">
                <PlanComponent
                  minimal
                  basic
                  upgradeRef={upgradeRef}
                  planCallback={_planCallback}
                  manageRef={billingManageRef}
                />
                <AffiliateComponent />
              </YStack>
            </YStack>
          </Tabs.Content>

          {/* Security Tab */}
          <Tabs.Content value="security" flex={1}>
                  <ErrorBoundary>
            <YStack gap="$4">
              <H6 style={{
                fontSize: 20,
                color: 'rgb(90, 192, 234)',
                fontWeight: 'bold',
                marginBottom: 8
              }}>
                Security & Privacy
              </H6>

              <YStack gap="$3">
                {/* MFA Component */}
                <View style={{
                          ...getCardStyle(theme)
                }}>
                  {/* <H6 style={{
                    fontSize: 18,
                    color: 'rgb(90, 192, 234)',
                    fontWeight: 'bold',
                    marginBottom: 16
                  }}>
                    {t('SettingsMFAHeader')}
                  </H6> */}
                  <MfaComponent />
                </View>

                {/* Password Component - Only on non-web platforms */}
                {Platform.OS !== 'web' && (
                  <View style={{
                            ...getCardStyle(theme)
                  }}>
                    <PassComponent />
                  </View>
                )}

                {/* Disable Invitations */}
                <View style={{
                          ...getCardStyle(theme)
                }}>
                  {/* <H6 style={{
                    fontSize: 18,
                    color: 'rgb(90, 192, 234)',
                    fontWeight: 'bold',
                    marginBottom: 16
                  }}>
                    {t("SettingsDisableInv")}
                  </H6> */}
                  <RowComponent
                    forceColumn
                    title={t("SettingsDisableInv")}
                    buttonText={t("Disable")}
                    subtitle={t("disableInvSub")}
                            icon={<Feather name="eye-off" size={Platform.OS === 'web' ? 20 : 24} color="white" />}
                    style={{
                      marginVertical: 0,
                    }}
                    onPress={async () => {
                      // TODO: Implement disable invitations API call
                      dispatch({
                        type: SHOW_AUTH_MODAL,
                        data: {
                          content: 'Disable invitations feature coming soon',
                          type: 'info'
                        }
                      });
                    }}
                  />
                </View>

                {/* Connected Devices */}
                <View style={{
                          ...getCardStyle(theme)
                }}>
                  <H6 style={{ marginBottom: 16, color: 'rgb(90, 192, 234)', fontWeight: 'bold' }}>
                    Connected Devices
                  </H6>
                  <XStack alignSelf='stretch' justifyContent='space-between' gap="$2">
                    {deviceList.map(device => (
                      <DeviceItem key={device.name} device={device} />
                    ))}
                  </XStack>
                </View>
              </YStack>
            </YStack>
                  </ErrorBoundary>
          </Tabs.Content>

          {/* Analytics Tab */}
          <Tabs.Content value="analytics" flex={1}>
            <YStack gap="$4">
              <H6 style={{
                fontSize: 20,
                color: 'rgb(90, 192, 234)',
                fontWeight: 'bold',
                marginBottom: 8
              }}>
                Usage Analytics
              </H6>

              <View style={{
                backgroundColor: theme ? 'rgba(248, 250, 252, 0.8)' : 'rgba(30, 30, 30, 0.8)',
                borderRadius: 16,
                padding: 20,
                borderWidth: 1,
                borderColor: theme ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)',
                minHeight: 400,
              }}>
                <React.Suspense fallback={
                  <View style={{ alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
                    <AnimatedLottieView
                      source={require("@assets/animations/leaf_Loader.json")}
                      autoPlay
                      style={{
                        alignSelf: "center",
                        justifyContent: "center",
                        minHeight: 120,
                        minWidth: 120,
                        maxWidth: 180
                      }}
                      loop
                    />
                    <Text style={{
                      marginTop: 16,
                      color: theme ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.6)',
                      fontSize: 16
                    }}>
                      Loading analytics...
                    </Text>
                  </View>
                }>
                  <UsageStatMonitor />
                </React.Suspense>
              </View>
            </YStack>
          </Tabs.Content>
        </Tabs>
      </Card>
        </ScrollView>
      }
    </XStack>
  );

  // Return the layout with proper scroll handling
  return (
    <View style={{ flex: 1 }}>
      {isTablet ? (
        // Tablet: Use regular View without outer ScrollView
        renderContent()
      ) : (
        // Mobile: Wrap in ScrollView for vertical scrolling
          <View
            style={{ flex: 1, alignSelf: 'stretch' }}
        >
          {renderContent()}
          </View>
      )}
    </View>
  );
}

export default ProfileSection;